import { useTheme } from "@-/dev.improve.haniverse.lib.theme";

export interface ChinaMapColors {
  /** Text and leaders over the sea, whose background follows the theme. */
  calloutLabel: string;
  /** Thin lines between cities and districts. */
  cityBorder: string;
  /** The lines between the focused province's cities, dark enough to read them apart. */
  focusedCityBorder: string;
  label: string;
  /** Every province and city, unless its province qualifies for progress shading. */
  land: string;
  /** Locked map glyphs remain neutral, but visibly lighter than learned glyphs. */
  lockedLabel: string;
  /** The center of the learner's location dot. */
  marker: string;
  /** The ring around the location dot. */
  markerOutline: string;
  /** Hairlines along province boundaries, drawn over the city borders. */
  provinceBorder: string;
  /** The line of a journey in progress. */
  route: string;
  /** Muted province and city borders outside the selected province. */
  unfocusedBorder: string;
  /** Very slightly off-white land outside the selected province. */
  unfocusedLand: string;
  /** Province progress shading (see `isChinaMapProvinceShaded`). */
  unlockedLand: string;
}

/**
 * Concrete map colors shared by the native and web renderers. The sea behind the regions is the
 * viewport's near-white background, keeping the light theme subtle.
 */
export function useChinaMapColors(): ChinaMapColors {
  const theme = useTheme();
  const isDark = theme === "dark";
  return {
    calloutLabel: isDark ? "#fafafa" : "#171717",
    // Very light, so city lines never compete with the province borders or the names over them.
    cityBorder: isDark ? "#d4d4d4" : "#e5e5e5",
    focusedCityBorder: isDark ? "#404040" : "#737373",
    label: "#000000",
    land: isDark ? "#a3a3a3" : "#ffffff",
    lockedLabel: "#404040",
    // Saturated, so the small dot stands out on white, yellow and gray land alike.
    marker: "#f97316",
    markerOutline: "#ffffff",
    // Pure black on both themes: the land, including the dark theme's gray, is light.
    provinceBorder: "#000000",
    route: "#dc2626",
    // The old 12% black selection overlay's gray now belongs on unfocused borders only.
    unfocusedBorder: "#e0e0e0",
    unfocusedLand: "#fafafa",
    unlockedLand: "#fef3a6",
  };
}
